import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ThemeToggle from '../components/ThemeToggle';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {/* Back Button - Top Left */}
      <div className="absolute top-4 left-4 z-10">
        <Link
          to="/login"
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Voltar
        </Link>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            Termos de Uso
          </h1>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Última atualização: 26 de agosto de 2025
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                1. Aceitação dos Termos
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Ao acessar e utilizar o Upmindy, você concorda em cumprir e estar vinculado a estes Termos de Uso.
                Se você não concordar com qualquer parte destes termos, não poderá utilizar nossa plataforma.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                2. Descrição do Serviço
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                O Upmindy é uma plataforma de aprendizado baseada em flashcards que utiliza técnicas de
                repetição espaçada para otimizar o processo de memorização e aprendizado. Nossa plataforma 
                permite que os usuários criem, organizem e estudem conjuntos de cards de estudo.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                3. Conta do Usuário
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>
                  Para utilizar nossos serviços, você deve criar uma conta fornecendo informações precisas e atualizadas.
                </p>
                <p>
                  Você é responsável por manter a confidencialidade de sua senha e por todas as atividades 
                  que ocorrem em sua conta.
                </p>
                <p>
                  Você deve nos notificar imediatamente sobre qualquer uso não autorizado de sua conta.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                4. Uso Aceitável
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>Você concorda em usar o Upmindy apenas para fins legais e de acordo com estes Termos. É proibido:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Usar o serviço para qualquer propósito ilegal ou não autorizado</li>
                  <li>Violar qualquer lei local, estadual, nacional ou internacional</li>
                  <li>Transmitir qualquer material prejudicial, ameaçador, abusivo ou difamatório</li>
                  <li>Interferir ou interromper o serviço ou servidores conectados ao serviço</li>
                  <li>Tentar obter acesso não autorizado a qualquer parte do serviço</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                5. Propriedade Intelectual
              </h2>
              <div className="text-gray-700 dark:text-gray-300 space-y-4">
                <p>
                  O Upmindy e seu conteúdo original, recursos e funcionalidades são e permanecerão propriedade
                  exclusiva da nossa empresa e seus licenciadores.
                </p>
                <p>
                  Você mantém todos os direitos sobre o conteúdo que cria e carrega na plataforma, 
                  mas nos concede uma licença para usar esse conteúdo para fornecer nossos serviços.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                6. Privacidade
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Sua privacidade é importante para nós. Nossa{' '}
                <Link 
                  to="/privacy-policy" 
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Política de Privacidade
                </Link>
                {' '}explica como coletamos, usamos e protegemos suas informações.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                7. Limitação de Responsabilidade
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Em nenhuma circunstância o Upmindy ou seus fornecedores serão responsáveis por quaisquer
                danos diretos, indiretos, incidentais, especiais, consequenciais ou punitivos, incluindo 
                perda de lucros, dados, uso, boa vontade ou outras perdas intangíveis.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                8. Encerramento
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Podemos encerrar ou suspender sua conta e acesso ao serviço imediatamente, sem aviso prévio 
                ou responsabilidade, por qualquer motivo, incluindo violação dos Termos de Uso.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                9. Modificações dos Termos
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Reservamo-nos o direito de modificar estes termos a qualquer momento. Se fizermos alterações 
                materiais, notificaremos você através da plataforma ou por e-mail. O uso continuado da 
                plataforma após as alterações constitui aceitação dos novos termos.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                10. Lei Aplicável
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Estes Termos são regidos pelas leis do Brasil. Qualquer disputa decorrente destes termos 
                será resolvida nos tribunais competentes do Brasil.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                11. Contato
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Se você tiver dúvidas sobre estes Termos de Uso, entre em contato conosco através da 
                plataforma ou pelos meios de contato disponibilizados.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
